import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { StorePicker } from '../../components/StorePicker';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import {
  ControlPogResetTasksResponse,
  ControlPogResetTaskSummary,
  PogResetTaskStatusFilter,
  PogResetTaskTab,
  Store,
} from '../../types';
import { PogResetFlagBadge } from './components/PogResetFlagBadge';
import { PogResetMappingActions } from './components/PogResetMappingActions';
import { usePogResetMappingRun } from './hooks/usePogResetMappingRun';

const STATUS_FILTERS: PogResetTaskTab[] = ['open', 'automated', 'closed'];

// Compact labels for the subordinate issue chips shown next to the status badge.
// A task can be "matched" (the matcher committed a proposal, score >= the 10% compute-time
// floor) while every mod is still flagged no_match (best candidate < the 70% read-time
// threshold). Surface that gap so "matched" never reads as "verified".
const LOW_CONFIDENCE_THRESHOLD = 0.7;

const ISSUE_SHORT_LABELS: Record<string, string> = {
  wrong_sequence: 'Wrong seq',
  multiple_sequences: '2 seq',
  duplicate_candidate: 'Duplicate',
  multiple_similar_candidates: 'Similar',
  missing_candidate: 'Missing',
};

// Descending status sort walks from "needs attention" to "done", matching how the
// numeric columns put the interesting rows on top first.
const STATUS_SORT_ORDER = ['matched', 'partially_matched', 'no_match', 'not_processed'];

type SortKey =
  | 'averageScore'
  | 'averageCoverage'
  | 'scannedModCount'
  | 'signedOffModCount'
  | 'status';

const isSortKey = (value: string): value is SortKey =>
  value === 'averageScore' ||
  value === 'averageCoverage' ||
  value === 'scannedModCount' ||
  value === 'signedOffModCount' ||
  value === 'status';

type ReviewedFilter = 'all' | 'reviewed' | 'not_reviewed';

const isReviewedFilter = (value: string | null): value is ReviewedFilter =>
  value === 'all' || value === 'reviewed' || value === 'not_reviewed';

const REVIEWED_FILTER_KEY = 'pogReset.tasks.reviewedFilter';

const REVIEWED_FILTERS: { value: ReviewedFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'not_reviewed', label: 'Not reviewed' },
  { value: 'reviewed', label: 'Reviewed' },
];

export const PogResetPage: React.FC = () => {
  const { storeName } = useParams<{ storeName: string }>();
  const navigate = useNavigate();
  const { token, legacyToken } = useAuthContext();
  const { toggleLoading, showErrorToast, showSuccessToast } = useNotificationContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [store, setStore] = useState<Store | null>(null);
  const [allStores, setAllStores] = useState<Store[]>([]);
  const [tasksResponse, setTasksResponse] = useState<ControlPogResetTasksResponse | null>(null);
  // Once a store is picked the list narrows to its chain; before that every store is on offer,
  // since there is no chain to narrow to yet.
  const pickerStores = useMemo(
    () => (store ? allStores.filter((s) => s.chainId === store.chainId) : allStores),
    [allStores, store]
  );
  // Filters/sort/search live in the URL so views are shareable and survive refresh.
  const [statusFilter, setStatusFilter] = useState<PogResetTaskTab>(() => {
    const value = searchParams.get('status');
    return value === 'closed' || value === 'automated' ? value : 'open';
  });
  // 'automated' is a view over open tasks, so it fetches the same list as 'open'.
  const fetchStatus: PogResetTaskStatusFilter = statusFilter === 'closed' ? 'closed' : 'open';
  const [badgeFilter, setBadgeFilter] = useState<Set<string>>(
    () => new Set((searchParams.get('badges') ?? '').split(',').filter(Boolean))
  );
  const [scoreSort, setScoreSort] = useState<{
    key: SortKey;
    dir: 'desc' | 'asc';
  } | null>(() => {
    const [key, dir] = (searchParams.get('sort') ?? '').split(':');
    if (isSortKey(key) && (dir === 'asc' || dir === 'desc')) {
      return { key, dir };
    }
    // Scan-confirmed tasks are the trustworthy ones — surface them first by default.
    return { key: 'scannedModCount', dir: 'desc' };
  });
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  // A URL param wins so shared links open the view they describe; otherwise the last
  // choice this user made is restored.
  const [reviewedFilter, setReviewedFilter] = useState<ReviewedFilter>(() => {
    const param = searchParams.get('reviewed');
    // 'hide' is the legacy value from when this was a "Hide reviewed" checkbox.
    if (param === 'hide') {
      return 'not_reviewed';
    }

    if (isReviewedFilter(param)) {
      return param;
    }

    const stored = localStorage.getItem(REVIEWED_FILTER_KEY);
    return isReviewedFilter(stored) ? stored : 'all';
  });

  useEffect(() => {
    try {
      localStorage.setItem(REVIEWED_FILTER_KEY, reviewedFilter);
    } catch {
      // Storage unavailable — the filter still works for this session.
    }
  }, [reviewedFilter]);
  const isFirstRunRef = useRef(true);

  // Reflect the current view in the URL (replace, to avoid history spam).
  useEffect(() => {
    const params: Record<string, string> = {};
    if (statusFilter !== 'open') params.status = statusFilter;
    if (badgeFilter.size > 0) params.badges = [...badgeFilter].join(',');
    if (scoreSort) params.sort = `${scoreSort.key}:${scoreSort.dir}`;
    if (search.trim()) params.q = search.trim();
    if (reviewedFilter !== 'all') params.reviewed = reviewedFilter;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, badgeFilter, scoreSort, search, reviewedFilter]);

  const mappingRun = usePogResetMappingRun({
    storeName,
    onRunComplete: () => loadData(true),
  });

  const loadData = async (silent = false) => {
    if (!silent) toggleLoading(true);
    try {
      const stores = await ApiService.fetchStores(token);
      setAllStores(stores);
      // Reached via the store-less /pog-reset shortcut: show the store picker only.
      if (!storeName) return;
      const currentStore = stores.find((s) => s.storeName === storeName);
      if (!currentStore) {
        showErrorToast('Store not found');
        navigate('/stores');
        return;
      }
      setStore(currentStore);
      const response = await ApiService.fetchPogResetTasks(token, storeName, fetchStatus);
      setTasksResponse(response);
      mappingRun.syncActiveRun(response.activeRun);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Error loading POG reset tasks');
    } finally {
      if (!silent) toggleLoading(false);
    }
  };

  useEffect(() => {
    setStore(null);
    setTasksResponse(null);
    // Keep URL-restored filters on first render; reset them on store/tab switches.
    if (!isFirstRunRef.current) {
      setBadgeFilter(new Set());
    }
    isFirstRunRef.current = false;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeName, token, fetchStatus]);

  const tasks = useMemo(() => tasksResponse?.tasks ?? [], [tasksResponse]);

  // "Generate mappings" (non-force) only computes open tasks without cached results —
  // hide it when there is nothing for it to do. "Recalculate everything" stays.
  const hasUnprocessedOpenTasks = tasks.some((task) => task.status === 'open' && !task.mapping);

  // Two axes per task (computed backend-side): rolled-up match status (triage)
  // + its issue flags (diagnosis).
  const taskStatus = (task: ControlPogResetTaskSummary): string =>
    task.mapping?.status ?? 'not_processed';
  const taskIssues = (task: ControlPogResetTaskSummary): string[] => task.mapping?.issues ?? [];
  const taskFilterBadges = (task: ControlPogResetTaskSummary): string[] => [
    taskStatus(task),
    ...taskIssues(task),
  ];

  // No PR candidates at all (skip-PR run / no inferences) — the averages come back null
  // rather than 0, so scores can't be read as a genuine "everything scored 0%".
  const isScanOnly = (task: ControlPogResetTaskSummary): boolean =>
    task.mapping != null && task.mapping.averageScore == null;

  // Only meaningful when the task claims a full match — no_match and partially_matched
  // already tell the reader the mapping is imperfect.
  const isLowConfidence = (task: ControlPogResetTaskSummary): boolean => {
    const average = task.mapping?.averageScore;
    return (
      taskStatus(task) === 'matched' &&
      average != null &&
      average < LOW_CONFIDENCE_THRESHOLD &&
      !isScanOnly(task)
    );
  };

  // Tasks the Automated tab operates on, before the badge/search/reviewed filters. Both
  // the tab badge and the chip counts are scoped to this, so they cannot disagree with
  // the table underneath them.
  const isAutomatedTab = statusFilter === 'automated';
  const scopedTasks = useMemo(
    () => (isAutomatedTab ? tasks.filter((task) => task.automationReady) : tasks),
    [tasks, isAutomatedTab]
  );

  const availableBadges = useMemo(() => {
    const counts = new Map<string, number>();
    scopedTasks.forEach((task) =>
      taskFilterBadges(task).forEach((badge) => counts.set(badge, (counts.get(badge) ?? 0) + 1))
    );
    // Status chips first (triage), then issue chips (diagnosis).
    const ordered = [
      'matched',
      'partially_matched',
      'no_match',
      'not_processed',
      'wrong_sequence',
      'multiple_sequences',
      'duplicate_candidate',
      'multiple_similar_candidates',
      'missing_candidate',
    ];
    const badges = [
      ...ordered.filter((badge) => counts.has(badge)),
      ...[...counts.keys()].filter((badge) => !ordered.includes(badge)),
    ];
    return badges.map((badge) => ({ badge, count: counts.get(badge) ?? 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedTasks]);

  // Automated tab: which tasks the reviewer ticked for bulk apply. Keyed by task id so a
  // reload or filter change cannot apply a task the user did not see selected.
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<number>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  const toggleTaskSelection = (taskId: number) => {
    setSelectedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }

      return next;
    });
  };

  const filteredTasks = useMemo(() => {
    const query = search.trim().toLowerCase();
    let matching =
      badgeFilter.size === 0
        ? scopedTasks
        : scopedTasks.filter((task) =>
            taskFilterBadges(task).some((badge) => badgeFilter.has(badge))
          );
    if (reviewedFilter === 'not_reviewed') {
      matching = matching.filter((task) => !task.reviewed);
    }

    if (reviewedFilter === 'reviewed') {
      matching = matching.filter((task) => task.reviewed);
    }
    if (query) {
      matching = matching.filter((task) =>
        [
          task.pogName,
          task.pogNumber,
          String(task.taskId),
          ...(task.mapping?.candidateTagNames ?? []),
        ].some((value) => value?.toLowerCase().includes(query))
      );
    }
    if (!scoreSort) return matching;
    const sortValue = (task: ControlPogResetTaskSummary): number => {
      if (scoreSort.key === 'status') {
        const index = STATUS_SORT_ORDER.indexOf(taskStatus(task));
        // Unknown statuses sort after the known ones in descending order.
        return index === -1 ? -1 : STATUS_SORT_ORDER.length - index;
      }

      return task.mapping?.[scoreSort.key] ?? -1;
    };
    return [...matching].sort((a, b) => {
      const valueA = sortValue(a);
      const valueB = sortValue(b);
      return scoreSort.dir === 'desc' ? valueB - valueA : valueA - valueB;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedTasks, badgeFilter, scoreSort, search, reviewedFilter]);

  const allAutomatedSelected =
    filteredTasks.length > 0 && filteredTasks.every((task) => selectedTaskIds.has(task.taskId));
  const selectedCount = filteredTasks.filter((task) => selectedTaskIds.has(task.taskId)).length;
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  // Apply each selected task's computed mapping exactly as the detail page's Confirm does:
  // start -> resolve APPROVED -> record the applied mapping. Strictly sequential, so a
  // failure stops the run with everything before it already applied.
  const handleBulkConfirm = async () => {
    setShowBulkConfirm(false);
    if (!storeName || bulkProgress) return;
    const queue = filteredTasks.filter(
      (task) => selectedTaskIds.has(task.taskId) && task.automationReady
    );
    if (queue.length === 0) return;

    setBulkProgress({ done: 0, total: queue.length });
    let applied = 0;
    try {
      for (const task of queue) {
        await ApiService.startPogReset(token, storeName, {
          taskId: task.taskId,
          tagNames: task.automationMods.map((mod) => mod.tagName),
          isMirrored: false,
        });
        await ApiService.resolvePogResetTask(legacyToken, storeName, task.taskId, {
          action: 'APPROVED',
          pogResetModUpdates: {},
        });
        try {
          await ApiService.savePogResetConfirmedMapping(token, storeName, task.taskId, {
            isMirrored: false,
            mods: task.automationMods.map((mod) => ({
              modNumber: mod.modNumber,
              tagName: mod.tagName,
              source: mod.source,
            })),
          });
        } catch {
          // The mapping was applied; only the audit record failed to save.
        }
        applied += 1;
        setBulkProgress({ done: applied, total: queue.length });
      }
      showSuccessToast(`Confirmed ${applied} of ${queue.length} mappings`);
    } catch (err) {
      showErrorToast(
        `${err instanceof Error ? err.message : 'Error confirming mappings'} — stopped after ${applied} of ${queue.length}`
      );
    } finally {
      setBulkProgress(null);
      setSelectedTaskIds(new Set());
      await loadData(true);
    }
  };

  const taskCounts = useMemo(
    () => ({
      total: tasks.length,
      processed: tasks.filter((task) => task.mapping).length,
      automated: tasks.filter((task) => task.automationReady).length,
    }),
    [tasks]
  );

  // How many of the loaded open tasks the backend judged applicable without review.
  const automationReadyCount = useMemo(
    () => tasks.filter((task) => task.automationReady).length,
    [tasks]
  );
  // Shown on the tab only when filters are hiding some of them, so the mismatch is
  // explained rather than looking like a bug.
  const hiddenAutomationCount = automationReadyCount - filteredTasks.length;

  // Most recent calculation timestamp across all rows.
  const lastComputedAt = useMemo(() => {
    const timestamps = tasks
      .map((task) => task.mapping?.computedAt)
      .filter((value): value is string => Boolean(value));
    if (timestamps.length === 0) return null;
    return timestamps.reduce((latest, value) => (value > latest ? value : latest));
  }, [tasks]);

  // Averages across the visible rows that have mappings.
  const visibleAverages = useMemo(() => {
    const scores = filteredTasks
      .map((task) => task.mapping?.averageScore)
      .filter((score): score is number => score != null);
    const coverages = filteredTasks
      .map((task) => task.mapping?.averageCoverage)
      .filter((coverage): coverage is number => coverage != null);
    if (scores.length === 0 && coverages.length === 0) return null;
    const mean = (values: number[]) =>
      values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
    return { score: mean(scores), coverage: mean(coverages), count: scores.length };
  }, [filteredTasks]);

  const cycleSort = (key: SortKey) => {
    setScoreSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'desc' };
      return prev.dir === 'desc' ? { key, dir: 'asc' } : null;
    });
  };

  const sortIndicator = (key: SortKey) =>
    scoreSort?.key === key ? (scoreSort.dir === 'desc' ? ' ↓' : ' ↑') : '';

  const toggleBadgeFilter = (badge: string) => {
    setBadgeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(badge)) next.delete(badge);
      else next.add(badge);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-[96rem] mx-auto">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <Link
              to={storeName ? `/stores/${storeName}` : '/stores'}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
            >
              <ArrowBackIcon />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Automated Planogram Reset</h1>
              <p className="text-sm text-gray-600">
                {storeName
                  ? `${store?.legibleName ?? storeName}${
                      store
                        ? ` · ${store.chainLegibleName} (${store.chainName}, ${store.storeName})`
                        : ''
                    }`
                  : 'Select a store to view its POG reset tasks'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {storeName && (
              <Link
                to={`/stores/${storeName}/pog-reset/install-map`}
                className="px-3 py-1.5 text-sm font-medium text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50"
                title="Store map with per-mod install status: plotted / camera installed / POG assigned"
              >
                Install map
              </Link>
            )}
            {storeName && (
              <Link
                to={`/stores/${storeName}/pog-reset/mods`}
                className="px-3 py-1.5 text-sm font-medium text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50"
                title="Inverted view: every camera-covered mod tag and its best planogram suggestion"
              >
                Mod tags view
              </Link>
            )}
            <StorePicker
              stores={pickerStores}
              current={store}
              onSelect={(s) => navigate(`/stores/${s.storeName}/pog-reset`)}
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="flex items-center gap-1">
              {STATUS_FILTERS.map((filter) => (
                <button
                  key={filter}
                  onClick={() => setStatusFilter(filter)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize ${
                    statusFilter === filter
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {filter}
                  {filter === 'automated' && automationReadyCount > 0 && (
                    <span className="ml-1.5 text-xs opacity-75">{automationReadyCount}</span>
                  )}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search POG name / number / task id / mod tag"
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
            <div className="flex items-center rounded-md border border-gray-300 overflow-hidden text-sm whitespace-nowrap">
              {REVIEWED_FILTERS.map(({ value, label }) => {
                const active = reviewedFilter === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setReviewedFilter(value)}
                    aria-pressed={active}
                    className={`px-2.5 py-1.5 border-l border-gray-300 first:border-l-0 transition-colors ${
                      active ? 'bg-gray-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <div className="flex-1" />
            {lastComputedAt && !mappingRun.isRunning && (
              <span
                className="text-xs text-gray-500 whitespace-nowrap"
                title="Most recent mapping calculation across the listed tasks"
              >
                Last calculated: {new Date(lastComputedAt).toLocaleString()}
              </span>
            )}
            <PogResetMappingActions
              run={mappingRun}
              showGenerate={hasUnprocessedOpenTasks}
              disabled={!store}
            />
          </div>

          {availableBadges.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mt-3 pt-3 border-t border-gray-100">
              {availableBadges.map(({ badge, count }) => (
                <button
                  key={badge}
                  onClick={() => toggleBadgeFilter(badge)}
                  className={`rounded-full ${
                    badgeFilter.has(badge)
                      ? 'ring-2 ring-blue-500 ring-offset-1'
                      : 'opacity-70 hover:opacity-100'
                  }`}
                  title="Filter by status"
                >
                  <PogResetFlagBadge flag={badge} suffix={`(${count})`} />
                </button>
              ))}
              {badgeFilter.size > 0 && (
                <button
                  onClick={() => setBadgeFilter(new Set())}
                  className="text-xs text-blue-600 hover:underline ml-1"
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Automation coverage. The percentage is of PROCESSED tasks, not of all: an
              unprocessed task has no mapping to judge, so counting it would understate
              how well the algorithm does on what it has actually seen. */}
          <div className="flex items-center gap-4 text-xs text-gray-600 mt-3 pt-3 border-t border-gray-100">
            <span>
              Total: <span className="font-semibold text-gray-800">{taskCounts.total}</span>
            </span>
            <span>
              Processed: <span className="font-semibold text-gray-800">{taskCounts.processed}</span>
            </span>
            <span>
              Automated:{' '}
              <span className="font-semibold text-green-700">{taskCounts.automated}</span>
              {taskCounts.processed > 0 && (
                <span className="text-gray-500">
                  {' '}
                  ({Math.round((taskCounts.automated / taskCounts.processed) * 100)}% of processed)
                </span>
              )}
            </span>
          </div>
        </div>

        {!storeName && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            No store selected — pick one from the store picker in the top-right corner.
          </div>
        )}

        {storeName && isAutomatedTab && (
          <div className="bg-white rounded-lg shadow-md p-3 mb-4 flex items-center gap-3">
            <button
              onClick={() => setShowBulkConfirm(true)}
              disabled={selectedCount === 0 || bulkProgress != null}
              className="px-4 py-2 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {bulkProgress
                ? `Confirming ${bulkProgress.done}/${bulkProgress.total}...`
                : `Confirm mappings (${selectedCount}/${filteredTasks.length})`}
            </button>
            <span className="text-xs text-gray-500">
              Applies each task&apos;s computed mapping one at a time, exactly as confirming it by
              hand would.
            </span>
            {hiddenAutomationCount > 0 && (
              <span className="text-xs text-amber-700">
                {hiddenAutomationCount} more automation-ready{' '}
                {hiddenAutomationCount === 1 ? 'task is' : 'tasks are'} hidden by the filters above.
              </span>
            )}
          </div>
        )}

        {storeName && (
          <div className="bg-white rounded-lg shadow-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-left text-gray-600">
                  {isAutomatedTab && (
                    <th className="px-4 py-3 font-medium w-10">
                      <input
                        type="checkbox"
                        checked={allAutomatedSelected}
                        onChange={() =>
                          setSelectedTaskIds(
                            allAutomatedSelected
                              ? new Set()
                              : new Set(filteredTasks.map((task) => task.taskId))
                          )
                        }
                        disabled={filteredTasks.length === 0 || bulkProgress != null}
                        title={allAutomatedSelected ? 'Clear selection' : 'Select all listed tasks'}
                        className="cursor-pointer"
                      />
                    </th>
                  )}
                  <th className="px-4 py-3 font-medium">Task ID</th>
                  <th className="px-4 py-3 font-medium">POG Start date</th>
                  <th className="px-4 py-3 font-medium">Planogram</th>
                  <th className="px-4 py-3 font-medium">Mods</th>
                  <th className="px-4 py-3 font-medium">Task</th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => cycleSort('status')}
                      className="hover:text-gray-900"
                      title="Rolled-up match status, ordered matched → partially matched → no match → not processed"
                    >
                      Status{sortIndicator('status')}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => cycleSort('averageScore')}
                      className="hover:text-gray-900"
                      title="Mean best-candidate score across mods (how purely camera mods belong to the planogram)"
                    >
                      Avg Score{sortIndicator('averageScore')}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => cycleSort('averageCoverage')}
                      className="hover:text-gray-900"
                      title="Mean planogram coverage across mods (matched / planogram items)"
                    >
                      Avg Coverage{sortIndicator('averageCoverage')}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => cycleSort('scannedModCount')}
                      className="hover:text-gray-900"
                      title="Mods mapped by field scans during camera installation"
                    >
                      Scanned{sortIndicator('scannedModCount')}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">
                    <button
                      onClick={() => cycleSort('signedOffModCount')}
                      className="hover:text-gray-900"
                      title="Proposed mods whose aisle a technician signed off as installed"
                    >
                      Signed off{sortIndicator('signedOffModCount')}
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">Mappings</th>
                </tr>
              </thead>
              <tbody>
                {filteredTasks.map((task) => (
                  <tr
                    key={task.taskId}
                    onClick={() =>
                      navigate(`/stores/${storeName}/pog-reset/${task.taskId}`, {
                        state: { taskIds: filteredTasks.map((t) => t.taskId) },
                      })
                    }
                    className={`border-b last:border-b-0 hover:bg-gray-50 cursor-pointer ${
                      task.reviewed ? 'opacity-50' : ''
                    }`}
                  >
                    {isAutomatedTab && (
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedTaskIds.has(task.taskId)}
                          onChange={() => toggleTaskSelection(task.taskId)}
                          disabled={bulkProgress != null}
                          className="cursor-pointer"
                        />
                      </td>
                    )}
                    <td className="px-4 py-3 font-mono">{task.taskId}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{task.pogStartDate ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div>{task.pogName ?? '—'}</div>
                      <div className="text-xs text-gray-500">{task.pogNumber ?? ''}</div>
                    </td>
                    <td className="px-4 py-3">{task.modCount ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            task.status === 'open'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {task.status}
                        </span>
                        {task.reviewed && (
                          <span
                            className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                            title={
                              task.reviewedAt
                                ? `Reviewed ${new Date(task.reviewedAt).toLocaleString()}`
                                : 'Reviewed'
                            }
                          >
                            Reviewed
                          </span>
                        )}
                        {/* Visible on every tab, so the Open list shows what the Automated
                            tab would pick up without switching to it. */}
                        {task.automationReady && (
                          <span
                            className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 cursor-help"
                            title="Every mod proposed at 50%+ confidence with no issues, every aisle signed off — can be applied without review"
                          >
                            Automated
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <PogResetFlagBadge flag={taskStatus(task)} />
                        {isLowConfidence(task) && (
                          <span
                            className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 whitespace-nowrap"
                            title={`The matcher proposed a mapping, but its average score (${Math.round(
                              (task.mapping?.averageScore ?? 0) * 100
                            )}%) is below the ${Math.round(
                              LOW_CONFIDENCE_THRESHOLD * 100
                            )}% match threshold — verify before confirming.`}
                          >
                            Low confidence
                          </span>
                        )}
                        {taskIssues(task).length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap opacity-80">
                            {taskIssues(task).map((issue) => (
                              <span
                                key={issue}
                                className="text-[11px] text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 whitespace-nowrap"
                                title={issue}
                              >
                                {ISSUE_SHORT_LABELS[issue] ?? issue}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {task.mapping?.averageScore != null
                        ? `${Math.round(task.mapping.averageScore * 100)}%`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {task.mapping?.averageCoverage != null
                        ? `${Math.round(task.mapping.averageCoverage * 100)}%`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {task.mapping && task.mapping.scannedModCount > 0 ? (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-800"
                          title="Mods mapped by field scans during camera installation"
                        >
                          {task.mapping.scannedModCount}/{task.mapping.scoredModCount}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {task.mapping && task.mapping.matchedModCount > 0 ? (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            task.mapping.signedOffModCount === task.mapping.matchedModCount
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                          title="Proposed mods whose aisle a technician signed off as installed"
                        >
                          {task.mapping.signedOffModCount}/{task.mapping.matchedModCount}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {task.mapping ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-gray-600">
                            {task.mapping.matchedModCount}/{task.mapping.scoredModCount} mods
                          </span>
                          {task.mapping.computedAt && (
                            <span
                              className="text-xs text-gray-400"
                              title={`run ${task.mapping.runId ?? ''} (${task.mapping.algorithmVersion ?? ''})`}
                            >
                              {new Date(task.mapping.computedAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {tasksResponse && filteredTasks.length === 0 && (
                  <tr>
                    <td
                      colSpan={isAutomatedTab ? 12 : 11}
                      className="px-4 py-8 text-center text-gray-500"
                    >
                      {statusFilter === 'automated'
                        ? 'No tasks are ready for automation: each needs every mod proposed at 50%+ confidence, no issues, every aisle signed off, and a planogram not excluded by name.'
                        : `No ${statusFilter} POG reset tasks found for this store.`}
                    </td>
                  </tr>
                )}
              </tbody>
              {visibleAverages && (
                <tfoot>
                  <tr className="border-t bg-gray-50 font-medium">
                    <td colSpan={6} className="px-4 py-3 text-right text-gray-600">
                      Average of {visibleAverages.count} mapped task
                      {visibleAverages.count === 1 ? '' : 's'}:
                    </td>
                    <td className="px-4 py-3">
                      {visibleAverages.score != null
                        ? `${Math.round(visibleAverages.score * 100)}%`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {visibleAverages.coverage != null
                        ? `${Math.round(visibleAverages.coverage * 100)}%`
                        : '—'}
                    </td>
                    <td />
                    <td />
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        {showBulkConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => setShowBulkConfirm(false)}
          >
            <div
              className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-3">Confirm mappings</h3>
              <p className="text-sm text-gray-700">
                You are about to apply the computed mapping to{' '}
                <span className="font-semibold">
                  {selectedCount} {selectedCount === 1 ? 'task' : 'tasks'}
                </span>
                . Do you want to continue?
              </p>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setShowBulkConfirm(false)}
                  className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkConfirm}
                  className="px-4 py-2 rounded-md text-sm font-medium bg-green-600 text-white hover:bg-green-700"
                >
                  Confirm {selectedCount} {selectedCount === 1 ? 'task' : 'tasks'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
